import React, { useCallback, useEffect, useState } from 'react';
import { getSessionUser } from "../../../utils/auth/sessionUser";
import {
	Badge,
	Button,
	Space,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
	CheckSquareOutlined,
	ClockCircleOutlined,
	EyeOutlined,
	FileTextOutlined,
	PlusOutlined,
	UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import Layout from '../../../components/Layout';
import {
	DataTable,
	PageToolbar,
} from '../../../components/shared';

import {
	fetchPendingDashboard,
	fetchPlan,
} from '../../../api/healthcare/TreatmentPlanApi';
import { fetchPatient } from '../../../api/healthcare/PatientApi';

import {
	ActivePlanOverview,
	PendingDashboardData,
	PendingPractitionerTask,
	PatientFormWaiting,
} from '../../../types/healthcare/TreatmentPlanTypes';
import { User } from '../../../types/admin/UserTypes';
import ProgressDots from './planComponents/planDrawerComponents/workflowProgress/ProgressDots';

import { useNotifications } from '../../../context/NotificationContext';
import { PlanDetailDrawer } from './planComponents/PlanDetailDrawer';
import CreateTreatmentPlanModal from './planComponents/CreateTreatmentPlanModal';
import EditPatientModal from '../patient/PatientModal';
import { Patient } from '../../../types/healthcare/PatientTypes';
import type { UserUuid } from '../../../types/identity';

dayjs.extend(relativeTime);

const { Text } = Typography;

// ── Local rail components ─────────────────────────────────────────────────────

interface TaskRailSectionProps {
	title: string;
	count: number;
	badgeColor?: string;
	children: React.ReactNode;
	emptyText?: string;
}

const TaskRailSection: React.FC<TaskRailSectionProps> = ({
	title,
	count,
	badgeColor = 'orange',
	children,
	emptyText,
}) => (
	<div style={{ display: 'flex', flexDirection: 'column' }}>
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				padding: '8px 10px 6px',
				borderBottom: '1px solid var(--mg-border)',
			}}
		>
			<Text
				style={{
					fontSize: 10,
					fontWeight: 700,
					textTransform: 'uppercase',
					letterSpacing: '0.06em',
					color: 'var(--mg-text-secondary)',
				}}
			>
				{title}
			</Text>
			<Badge
				count={count}
				style={{
					backgroundColor: badgeColor === 'blue' ? 'var(--mg-primary)' : 'var(--mg-warning)',
					fontSize: 10,
					height: 16,
					lineHeight: '16px',
					minWidth: 16,
				}}
			/>
		</div>
		<div style={{ overflowY: 'auto' }}>
			{count === 0 ? (
				<div style={{ padding: '10px', textAlign: 'center' }}>
					<Text type="secondary" style={{ fontSize: 11 }}>
						{emptyText ?? 'None'}
					</Text>
				</div>
			) : (
				children
			)}
		</div>
	</div>
);

interface TaskRailItemProps {
	task: PendingPractitionerTask;
	patientName: string;
	loading: boolean;
	onAction: () => void;
}

const TaskRailItem: React.FC<TaskRailItemProps> = ({ task, patientName, loading, onAction }) => (
	<div
		style={{
			padding: '7px 10px',
			borderBottom: '1px solid var(--mg-border)',
			display: 'flex',
			flexDirection: 'column',
			gap: 3,
		}}
	>
		<div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
			<CheckSquareOutlined
				style={{ fontSize: 11, color: 'var(--mg-warning)', marginTop: 2, flexShrink: 0 }}
			/>
			<Text
				style={{
					fontSize: 11,
					fontWeight: 500,
					color: 'var(--mg-text)',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
					flex: 1,
				}}
				title={task.task_title ?? 'Task'}
			>
				{task.task_title ?? 'Task'}
			</Text>
		</div>
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: 4,
			}}
		>
			<div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
				<UserOutlined style={{ fontSize: 10, color: 'var(--mg-text-secondary)' }} />
				<Text
					type="secondary"
					style={{
						fontSize: 10,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{patientName}
				</Text>
			</div>
			<div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
				<Tooltip title={dayjs(task.entered_at).format('DD MMM YYYY HH:mm')}>
					<Text type="secondary" style={{ fontSize: 10 }}>
						<ClockCircleOutlined style={{ marginRight: 2 }} />
						{dayjs(task.entered_at).fromNow(true)}
					</Text>
				</Tooltip>
				<Button
					type="primary"
					size="small"
					style={{ fontSize: 10, height: 22, padding: '0 6px' }}
					loading={loading}
					onClick={onAction}
				>
					Open
				</Button>
			</div>
		</div>
	</div>
);

interface WaitingRailItemProps {
	item: PatientFormWaiting;
	patientName: string;
	loading: boolean;
	onView: () => void;
}

const WaitingRailItem: React.FC<WaitingRailItemProps> = ({ item, patientName, loading, onView }) => (
	<div
		style={{
			padding: '7px 10px',
			borderBottom: '1px solid var(--mg-border)',
			display: 'flex',
			flexDirection: 'column',
			gap: 3,
		}}
	>
		<div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
			<FileTextOutlined
				style={{ fontSize: 11, color: 'var(--mg-primary)', marginTop: 2, flexShrink: 0 }}
			/>
			<Text
				style={{
					fontSize: 11,
					fontWeight: 500,
					color: 'var(--mg-text)',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
					flex: 1,
				}}
				title={item.form_name ?? 'Form'}
			>
				{item.form_name ?? 'Form'}
			</Text>
		</div>
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: 4,
			}}
		>
			<div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
				<UserOutlined style={{ fontSize: 10, color: 'var(--mg-text-secondary)' }} />
				<Text
					type="secondary"
					style={{
						fontSize: 10,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{patientName}
				</Text>
			</div>
			<div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
				<Tooltip title={dayjs(item.entered_at).format('DD MMM YYYY HH:mm')}>
					<Text type="secondary" style={{ fontSize: 10 }}>
						<ClockCircleOutlined style={{ marginRight: 2 }} />
						{dayjs(item.entered_at).fromNow(true)}
					</Text>
				</Tooltip>
				<Button
					size="small"
					style={{ fontSize: 10, height: 22, padding: '0 6px' }}
					loading={loading}
					onClick={onView}
				>
					View
				</Button>
			</div>
		</div>
	</div>
);

// ── Page ─────────────────────────────────────────────────────────────────────

const PeptideDashboardPage: React.FC = () => {
	const user = getSessionUser() as User;
	const { showError } = useNotifications();

	const [loading, setLoading] = useState(true);
	const [dashboard, setDashboard] = useState<PendingDashboardData | null>(null);
	const [patientMap, setPatientMap] = useState<Record<UserUuid, Patient>>({});

	// PlanDetailDrawer state
	const [drawerPlan, setDrawerPlan] = useState<import('../../../types/healthcare/TreatmentPlanTypes').PatientTreatmentPlan | null>(null);
	const [drawerLoading, setDrawerLoading] = useState<number | null>(null);
	const [drawerTargetStageLogId, setDrawerTargetStageLogId] = useState<number | null>(null);

	// Show cancelled plans toggle (mirrors the Patients page "Show Deactivated")
	const [showCancelled, setShowCancelled] = useState(false);

	// Create modal
	const [createOpen, setCreateOpen] = useState(false);

	// Patient profile modal
	const [patientModal, setPatientModal] = useState<Patient | null>(null);

	// ── Data loading ──────────────────────────────────────────────

	const loadDashboard = useCallback(async () => {
		setLoading(true);
		try {
			const data = await fetchPendingDashboard(showCancelled);
			setDashboard(data);

			const patientIds = Array.from(
				new Set([
					...data.practitioner_tasks.map((t) => t.patient_id),
					...data.patient_forms_waiting.map((f) => f.patient_id),
					...(data.active_plans ?? []).map((p) => p.patient_id),
				])
			);
			const results = await Promise.allSettled(
				patientIds.map((id) => fetchPatient(String(id)))
			);
			const map: Record<UserUuid, Patient> = {};
			results.forEach((r, i) => {
				if (r.status === 'fulfilled') map[patientIds[i]] = r.value.data;
			});
			setPatientMap(map);
		} catch (err: any) {
			showError(err?.response?.data?.message ?? 'Failed to load dashboard');
		} finally {
			setLoading(false);
		}
	}, [showError, showCancelled]);

	useEffect(() => {
		loadDashboard();
	}, [loadDashboard]);

	// ── Helpers ───────────────────────────────────────────────────

	const patientDisplayName = (id: UserUuid) => {
		const p = patientMap[id];
		if (!p) return `Patient #${id}`;
		return `${p.first_name} ${p.last_name}`;
	};

	// ── Handlers ──────────────────────────────────────────────────

	const openDrawer = async (planId: number, targetStageLogId?: number) => {
		setDrawerLoading(planId);
		try {
			const plan = await fetchPlan(planId);
			// Attach patient object if the API response didn't embed it
			if (!plan.patient && plan.patient_id) {
				const pt = patientMap[plan.patient_id];
				if (pt) {
					(plan as any).patient = pt;
				} else {
					try {
						const res = await fetchPatient(String(plan.patient_id));
						(plan as any).patient = res.data;
					} catch {
						// non-critical — drawer degrades gracefully
					}
				}
			}
			setDrawerTargetStageLogId(targetStageLogId ?? null);
			setDrawerPlan(plan);
		} catch {
			showError('Failed to load plan details');
		} finally {
			setDrawerLoading(null);
		}
	};

	// ── Active Plans columns ──────────────────────────────────────

	const activePlanColumns: ColumnsType<ActivePlanOverview> = [
		{
			title: 'Patient',
			key: 'patient',
			render: (_, r) => {
				const p = patientMap[r.patient_id];
				const age = p?.date_of_birth
					? dayjs().diff(dayjs(p.date_of_birth), 'year')
					: null;
				const sex = p?.sex ?? p?.gender ?? null;
				return (
					<div>
						<Text strong style={{ fontSize: 12 }}>
							{patientDisplayName(r.patient_id)}
						</Text>
						{(age || sex) && (
							<Text
								type="secondary"
								style={{ fontSize: 11, display: 'block' }}
							>
								{[age ? `${age}y` : null, sex].filter(Boolean).join(' · ')}
							</Text>
						)}
					</div>
				);
			},
		},
		{
			title: 'Plan',
			dataIndex: 'plan_name',
			key: 'plan_name',
			render: (v, r) => (
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
					<Text type="secondary" style={{ fontSize: 12 }}>
						{v ?? '—'}
					</Text>
					{r.status === 'cancelled' && (
						<Tag color="red" style={{ fontSize: 10, margin: 0 }}>Cancelled</Tag>
					)}
				</span>
			),
		},
		{
			title: 'Progress',
			key: 'progress',
			render: (_, r) => <ProgressDots stages={r.stages} sections={r.sections} />,
		},
		{
			title: 'Section',
			key: 'section',
			width: 130,
			render: (_, r) =>
				r.current_section ? (
					<Tag
						color={r.current_section.color}
						style={{ fontSize: 10, margin: 0 }}
					>
						{r.current_section.name}
					</Tag>
				) : (
					<Text type="secondary">—</Text>
				),
		},
		{
			title: '',
			key: 'actions',
			width: 60,
			render: (_, r) => (
				<Space size={4}>
					<Button
						type="text"
						size="small"
						icon={<UserOutlined />}
						onClick={(e) => {
							e.stopPropagation();
							const pt = patientMap[r.patient_id];
							if (pt) setPatientModal(pt);
						}}
					/>
					<Button
						type="text"
						size="small"
						icon={<EyeOutlined />}
						loading={drawerLoading === r.plan_id}
						onClick={(e) => {
							e.stopPropagation();
							openDrawer(r.plan_id);
						}}
					/>
				</Space>
			),
		},
	];

	const tasks = dashboard?.practitioner_tasks ?? [];
	const waitingForms = dashboard?.patient_forms_waiting ?? [];
	const activePlans = dashboard?.active_plans ?? [];

	// ── Render ────────────────────────────────────────────────────

	return (
		<Layout>
			<div
				style={{
					display: 'flex',
					height: 'calc(100vh - var(--mg-topbar-h, 56px))',
					overflow: 'hidden',
				}}
			>
				{/* Left action rail — 220px */}
				<div
					style={{
						width: 220,
						flexShrink: 0,
						borderRight: '1px solid var(--mg-border)',
						display: 'flex',
						flexDirection: 'column',
						overflowY: 'auto',
						background: 'var(--mg-bg-card)',
					}}
				>
					<TaskRailSection
						title="My Tasks"
						count={tasks.length}
						badgeColor="orange"
						emptyText="All caught up!"
					>
						{tasks.map((t) => (
							<TaskRailItem
								key={`task-${t.stage_log_id}-${t.plan_id}`}
								task={t}
								patientName={patientDisplayName(t.patient_id)}
								loading={drawerLoading === t.plan_id}
								onAction={() => openDrawer(t.plan_id, t.stage_log_id)}
							/>
						))}
					</TaskRailSection>

					<div style={{ borderTop: '1px solid var(--mg-border)' }}>
						<TaskRailSection
							title="Waiting on Patient"
							count={waitingForms.length}
							badgeColor="blue"
							emptyText="No pending forms"
						>
							{waitingForms.map((f) => (
								<WaitingRailItem
									key={`form-${f.plan_id}-${f.stage_log_id}-${f.item_key ?? f.form_id}`}
									item={f}
									patientName={patientDisplayName(f.patient_id)}
									loading={drawerLoading === f.plan_id}
									onView={() => openDrawer(f.plan_id, f.stage_log_id)}
								/>
							))}
						</TaskRailSection>
					</div>
				</div>

				{/* Right main — plans table */}
				<div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
					<PageToolbar
						primaryAction={{
							label: 'New Treatment Plan',
							icon: <PlusOutlined />,
							onClick: () => setCreateOpen(true),
						}}
						extraActions={[
							{
								label: showCancelled ? 'Hide Cancelled' : 'Show Cancelled',
								icon: <EyeOutlined />,
								onClick: () => setShowCancelled((v) => !v),
							},
						]}
					/>

					<div style={{ flex: 1, overflowY: 'auto' }}>
						<DataTable<ActivePlanOverview>
							rowKey={(r) => r.plan_id}
							columns={activePlanColumns}
							dataSource={activePlans}
							loading={loading}
							emptyTitle="No active plans"
							emptyDescription="No treatment plans are currently active"
							onRowClick={(r) => openDrawer(r.plan_id)}
							pagination={false}
						/>
					</div>
				</div>
			</div>

			{/* Plan Detail Drawer */}
			<PlanDetailDrawer
				plan={drawerPlan}
				initialSelectedStageLogId={drawerTargetStageLogId}
				onClose={() => setDrawerPlan(null)}
				onPlanChange={loadDashboard}
				showToolbar
			/>

			{/* Patient Profile Modal */}
			{patientModal && (
				<EditPatientModal
					isOpen
					patient={patientModal}
					onClose={() => setPatientModal(null)}
				/>
			)}

			{/* Create Treatment Plan Modal */}
			<CreateTreatmentPlanModal
				open={createOpen}
				onClose={() => setCreateOpen(false)}
				onCreated={() => {
					setCreateOpen(false);
					loadDashboard();
				}}
			/>
		</Layout>
	);
};

export default PeptideDashboardPage;
